import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from '@jobcost-monorepo/i18n';
import {
  cn,
  floatToHours,
  hoursToFloat,
  TIMESHEET_HOURS_REGEX,
} from '@jobcost-monorepo/utils';
import { ChangeEvent, InputHTMLAttributes, useEffect, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Input } from '../../atoms';

interface Props extends InputHTMLAttributes<HTMLInputElement> {
  name: string;
  errorName: string;
  type?: string;
  label?: string;
  width?: string;
}

export const HoursInputForm: React.FC<Props> = ({
  name,
  errorName,
  // Estratto per non finire in `rest`: l'input è sempre `type="text"` perché
  // le ore si scrivono nel formato hh:mm e vengono convertite in float.
  type,
  label,
  width = 'w-full',
  ...rest
}) => {
  const { t } = useTranslation('errors');

  const [inputValue, setInputValue] = useState<string>('');

  const { control, clearErrors, setError } = useFormContext();
  const {
    field: { onChange, value },
    formState: { errors },
  } = useController({ control, name });

  const error = errors?.[errorName]?.message ?? '';
  const errorTranslated = t(error as string, { ns: 'errors' });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!!errorName) {
      clearErrors(name);
    }
    setInputValue(event.target.value);
  };

  const handleBlur = () => {
    setInputValue(inputValue.trim());

    if (!TIMESHEET_HOURS_REGEX.test(inputValue)) {
      setError(name, {
        type: 'manual',
        message: 'hours_format',
      });

      return onChange(null);
    }

    const formattedHours = hoursToFloat(inputValue);

    onChange(formattedHours);
  };

  useEffect(() => {
    if (value) {
      setInputValue(floatToHours(value));
      onChange(value);
    } else {
      setInputValue('');
    }
  }, [value]);

  return (
    <div className={cn(width)}>
      {label && (
        <label
          htmlFor={name}
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          {label}
        </label>
      )}
      <div className="relative mt-1 rounded-md shadow-sm">
        <Input
          type="text"
          name={name}
          id={name}
          onChange={handleChange}
          onBlur={handleBlur}
          value={inputValue}
          className={cn(
            error && 'border-red text-red placeholder-red-300 dark:border-red',
          )}
          autoComplete="off"
          placeholder='"8h 30m", "8h", "30m"'
          {...rest}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          {errorTranslated ? (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red"
              aria-hidden="true"
            />
          ) : (
            <span className="h-5 w-5 " />
          )}
        </div>
      </div>
      <p className="h-4 text-xs my-1 ml-1 text-red overflow-hidden" id="error">
        {errorTranslated}
      </p>
    </div>
  );
};

export default HoursInputForm;
